import React from 'react';

import * as keyboard from 'util/Keyboard';

interface HandleMenuNavigationOptions {
  /** Keyboard event to handle */
  event: React.KeyboardEvent<HTMLElement>;
  /** Array of refs for focusable menu option elements */
  optionRefs: Array<React.RefObject<HTMLElement>>;
  /** Optional callback to close the menu when Escape is pressed */
  onClose?: () => void;
  /** Use left/right arrows instead of up/down for horizontal menus */
  horizontalMode?: boolean;
}

/**
 * Handles keyboard navigation for menus following ARIA Authoring Practices Guide.
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/menu/
 *
 * Supports:
 * - Arrow Up/Down (or Left/Right if horizontalMode): Navigate between options (with wrapping)
 * - Home/End: Jump to first/last option
 * - Enter/Space: Activate the focused option
 * - Escape: Close the menu
 */
export function handleMenuNavigation(options: HandleMenuNavigationOptions): void {
  const {event: e, optionRefs, onClose, horizontalMode = false} = options;

  const activeElement = document.activeElement;
  let currentIndex = -1;
  if (activeElement) {
    currentIndex = optionRefs.findIndex(
      (ref) => ref.current === activeElement || ref.current?.contains(activeElement)
    );
  }

  const optionCount = optionRefs.length;

  const isNextKey = horizontalMode ? keyboard.isRightKey(e) : keyboard.isDownKey(e);
  const isPrevKey = horizontalMode ? keyboard.isLeftKey(e) : keyboard.isUpKey(e);

  if (isNextKey) {
    e.preventDefault();
    e.stopPropagation();
    if (currentIndex < optionCount - 1) {
      optionRefs[currentIndex + 1]?.current?.focus();
    } else {
      // Wrap to first option
      optionRefs[0]?.current?.focus();
    }
  } else if (isPrevKey) {
    e.preventDefault();
    e.stopPropagation();
    if (currentIndex > 0) {
      optionRefs[currentIndex - 1]?.current?.focus();
    } else {
      // Wrap to last option
      optionRefs[optionCount - 1]?.current?.focus();
    }
  } else if (keyboard.isHomeKey(e)) {
    e.preventDefault();
    e.stopPropagation();
    optionRefs[0]?.current?.focus();
  } else if (keyboard.isEndKey(e)) {
    e.preventDefault();
    e.stopPropagation();
    optionRefs[optionCount - 1]?.current?.focus();
  } else if (keyboard.isEnterKey(e) || keyboard.isSpaceKey(e)) {
    if (currentIndex !== -1) {
      e.preventDefault();
      e.stopPropagation();
      optionRefs[currentIndex]?.current?.click();
    }
  } else if (keyboard.isEscapeKey(e) && onClose) {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  }
}

/**
 * Auto-focuses the first option in a menu for keyboard accessibility.
 * Should be called after the menu is mounted and refs are collected.
 */
export function autoFocusFirstOption(optionRefs: Array<React.RefObject<HTMLElement>>): void {
  if (optionRefs.length > 0 && optionRefs[0]?.current) {
    optionRefs[0].current.focus({preventScroll: true});
  }
}
